/* ============================================================
   THEME ANALYTICS CONTRACT
   Emits normalized, non-PII storefront events. This module does not call a
   vendor or create network requests. A consent-aware pixel can subscribe to
   `kp:analytics` and forward only the events it is allowed to process.
   ============================================================ */
(function () {
  var KPTheme = window.KPTheme = window.KPTheme || {};
  if (KPTheme.track || document.body?.dataset.pageType === '404') return;
  var allowedEvents = new Set([
    'page_view',
    'variant_change',
    'product_click',
    'predictive_search',
    'predictive_search_click',
    'collection_filter',
    'collection_load_more_attempt',
    'collection_load_more_success',
    'collection_load_more_failure',
    'finder_complete',
    'finder_result_click',
    'finder_restart',
    'cart_add_attempt',
    'cart_add_success',
    'cart_add_failure',
    'cart_change_attempt',
    'cart_change_success',
    'cart_change_failure',
    'cart_drawer_open',
    'checkout_begin'
  ]);
  var sequence = 0;

  function cleanValue(value) {
    if (value === null || value === undefined || value === '') return undefined;
    if (typeof value === 'boolean' || typeof value === 'number') return value;
    return String(value).slice(0, 160);
  }

  function pageContext() {
    var body = document.body;
    if (!body) return {};
    return {
      page_type: cleanValue(body.dataset.pageType),
      template_suffix: cleanValue(body.dataset.templateSuffix),
      product_id: cleanValue(body.dataset.productId),
      product_handle: cleanValue(body.dataset.productHandle),
      product_sku: cleanValue(body.dataset.productSku),
      product_price: cleanValue(body.dataset.productPrice),
      collection_handle: cleanValue(body.dataset.collectionHandle),
      currency: cleanValue((window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '')
    };
  }

  function track(eventName, properties) {
    if (!allowedEvents.has(eventName)) {
      if (window.Shopify && window.Shopify.designMode) {
        console.warn('Ignored unknown KP analytics event:', eventName);
      }
      return false;
    }

    var detail = Object.assign({
      event: eventName,
      event_id: 'kp-' + Date.now().toString(36) + '-' + (++sequence).toString(36)
    }, pageContext());

    Object.keys(properties || {}).forEach(function (key) {
      var value = cleanValue(properties[key]);
      if (value !== undefined) detail[key] = value;
      else delete detail[key];
    });

    document.dispatchEvent(new CustomEvent('kp:analytics', { detail: detail }));
    return true;
  }

  KPTheme.analyticsEvents = Object.freeze(Array.from(allowedEvents));
  KPTheme.track = track;

  // Use the request's surface and Shopify's successful response. A recommendation
  // can be a different product from the page; never inherit its SKU or price.
  KPTheme.source = function (target) {
    if (target?.closest('m-cart-drawer')) return 'cart_drawer';
    if (target?.closest('sticky-atc')) return 'sticky_atc';
    if (target?.closest('.m-product-card')) return 'collection_quick_add';
    if (target?.closest('m-product-quick-view, .m-product-quick-view')) return 'quick_view';
    return document.body.dataset.pageType === 'cart' ? 'cart_page' : 'product';
  };
  KPTheme.added = function (response, properties) {
    var items = response && (response.items || [response]);
    (items || []).forEach(function (item) {
      if (!item || !item.id) return;
      track('cart_add_success', Object.assign({}, properties, {
        product_id: item.product_id,
        product_handle: item.handle,
        product_sku: item.sku,
        product_price: typeof item.final_price === 'number' ? item.final_price / 100 :
          (typeof item.price === 'number' ? item.price / 100 : undefined),
        variant_id: item.variant_id || item.id,
        quantity: properties?.quantity || item.quantity || 1
      }));
    });
  };

  function closestProductLink(target) {
    return target.closest(
      '.m-product-card a[href*="/products/"], .kp-drec a[href*="/products/"],' +
      'predictive-search a[href*="/products/"],' +
      '.rp-card a[href*="/products/"],' +
      '.search-result-item[href*="/products/"],' +
      '[data-product-card] a[href*="/products/"]'
    );
  }

  document.addEventListener('variant:changed', function (event) {
    var variant = event.detail && event.detail.variant;
    if (!variant) return;
    track('variant_change', {
      variant_id: variant.id,
      available: Boolean(variant.available),
      price_minor: variant.price,
      product_sku: variant.sku,
      product_price: typeof variant.price === 'number' ? variant.price / 100 : undefined
    });
  });

  document.addEventListener('click', function (event) {
    if (!event.target || typeof event.target.closest !== 'function') return;

    var productLink = closestProductLink(event.target);
    if (productLink) {
      var card = productLink.closest('[data-product-id], [data-product-card], .m-product-card, .kp-drec');
      track(productLink.closest('predictive-search') ? 'predictive_search_click' : 'product_click', {
        target_product_id: card && card.dataset.productId,
        destination: productLink.pathname || productLink.getAttribute('href')
      });
    }

    /* Shop Pay/Apple Pay/Google Pay render inside Shopify's own
       <shopify-accelerated-checkout>/<shopify-accelerated-checkout-cart>
       custom elements (payment_button on PDPs, content_for_additional_
       checkout_buttons on cart/drawer). Those use shadow DOM internally, so
       a click anywhere inside gets retargeted by the browser to the custom
       element itself before this document-level listener ever sees it —
       matching the tag name here is reading that native retargeting, not
       piercing the shadow boundary with composedPath()/shadowRoot. */
    var checkout = event.target.closest(
      'a[href*="/checkout"], button[name="checkout"], input[name="checkout"],' +
      '.shopify-payment-button__button, [data-testid="PaymentButton"],' +
      'shopify-accelerated-checkout, shopify-accelerated-checkout-cart'
    );
    if (checkout && !checkout.disabled && checkout.getAttribute('aria-disabled') !== 'true') {
      track('checkout_begin', {
        source: KPTheme.source(checkout),
        accelerated: Boolean(checkout.closest('shopify-accelerated-checkout, shopify-accelerated-checkout-cart, .shopify-payment-button'))
      });
    }
  });

  document.addEventListener('change', function (event) {
    var filterForm = event.target.closest && event.target.closest('collection-filters-form, .m-collection-toolbar');
    if (!filterForm) return;
    var selectedFilterCount = filterForm.querySelectorAll(
      'input[type="checkbox"]:checked, input[type="radio"]:checked'
    ).length;
    var hasPriceFilter = Array.from(
      filterForm.querySelectorAll('input[type="number"], input[name*="price"]')
    ).some(function (input) {
      return input.value !== '';
    });
    track('collection_filter', {
      control_type: event.target.name === 'sort_by' ? 'sort' :
        (event.target.type === 'checkbox' ? 'filter' : 'price'),
      selected_filter_count: selectedFilterCount,
      has_price_filter: hasPriceFilter
    });
  }, true);

  function init() {
    track('page_view');
  }

  /* Wait for DOMContentLoaded unless the document is fully complete.
     The old branch ran init() immediately whenever readyState was past
     'loading' — but defer scripts execute while readyState is already
     'interactive' and BEFORE DOMContentLoaded, so the initial page_view was
     dispatched before later defer scripts (kp-analytics-beacon.js) had
     subscribed to the bus, and the event was silently lost. DOMContentLoaded
     fires only after every defer script has run, so all subscribers exist. */
  if (document.readyState === 'complete') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  }
})();
